import { Box, Typography } from '@mui/material';
import { forwardRef, memo, useCallback, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List,
  ListRowRenderer,
  WindowScroller,
} from 'react-virtualized';
import { debounce } from '../../../utils/debounce';

interface RowProps {
  row: any;
  style: CSSProperties;
  measure: () => void;
  registerChild: (element: HTMLDivElement | null) => void;
}

const Row = forwardRef<HTMLDivElement, RowProps>(function Row(
  { row, style, measure, registerChild },
  _ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = containerRef.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const handleResize = debounce(() => {
      measure();
    }, 150);

    const observer = new ResizeObserver(() => {
      handleResize();
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [measure]);

  const setRef = (el: HTMLDivElement | null) => {
    containerRef.current = el;
    registerChild(el);
  };

  return (
    <Box
      className="listItem"
      sx={{ ...style, paddingBottom: { xs: '16px', xl: '22px' } }}
      ref={setRef}
    >
      {row}
    </Box>
  );
});

interface InfiniteScrollProps {
  allRows: any[];
  totalRows: number;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}

const InfiniteScroll = ({
  allRows,
  totalRows,
  fetchNextPage,
  isFetchingNextPage,
}: InfiniteScrollProps) => {
  const listRef = useRef<List | null>(null);
  const cache = useRef<CellMeasurerCache>(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 300,
      minHeight: 300,
    })
  );

  const rowRenderer: ListRowRenderer = useCallback(
    ({ key, index, style, parent }) => (
      <CellMeasurer
        key={key}
        cache={cache.current}
        columnIndex={0}
        rowIndex={index}
        parent={parent}
      >
        {({ measure, registerChild }) => (
          <Row
            row={allRows[index]}
            style={style}
            measure={measure}
            registerChild={registerChild}
          />
        )}
      </CellMeasurer>
    ),
    [allRows]
  );

  const isRowLoaded = ({ index }: { index: number }): boolean => {
    return index < allRows.length;
  };

  const loadMoreRows = async ({
    startIndex: _startIndex,
    stopIndex: _stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    if (!isFetchingNextPage) {
      return fetchNextPage();
    }
    return Promise.resolve();
  };

  return (
    <Box className="container" width={1} height={1}>
      <Box className="list-wrapper" width={1}>
        <AutoSizer disableHeight>
          {({ width }) => (
            <WindowScroller>
              {({ height, isScrolling, onChildScroll, scrollTop }) => (
                <InfiniteLoader
                  isRowLoaded={isRowLoaded}
                  loadMoreRows={loadMoreRows}
                  rowCount={totalRows}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <List
                      ref={(instance) => {
                        listRef.current = instance; // Attach ref to list for manual recalculations
                        registerChild(instance);
                      }}
                      autoHeight
                      onRowsRendered={onRowsRendered}
                      height={height}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      rowCount={totalRows}
                      rowHeight={cache.current.rowHeight}
                      deferredMeasurementCache={cache.current}
                      rowRenderer={rowRenderer}
                      scrollTop={scrollTop}
                      width={width}
                      overscanRowCount={5}
                      scrollingResetTimeInterval={300}
                    />
                  )}
                </InfiniteLoader>
              )}
            </WindowScroller>
          )}
        </AutoSizer>
        {isFetchingNextPage && (
          <Typography variant="h3">Loading more data...</Typography>
        )}
      </Box>
    </Box>
  );
};

export default memo(InfiniteScroll);
